import React from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Plus, Trash2, Smartphone, AlertTriangle } from "lucide-react";

export const BlockMoviles = ({
  lines,
  setLines,
  products,
  bonoProducts,
  bonoCheck,
  existingLines,
  existingBonos,
  setExistingBonos,
  getProduct,
  fmtC,
  renderBlockHeader,
  openBlocks,
  pkgAValid,
  maxLines = Infinity,
  placeholder = "Selecciona móvil...",
  getPrice,
  disabledTip = "Requiere paquete convergente",
}) => {
  const priceOf = (p) => (getPrice ? getPrice(p) : (p?.precio_paquete ?? p?.precio_base ?? 0));
  const genKey = () => Math.random().toString(36).substr(2, 9);

  const updateLine = (index, field, value) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "serviceId") {
      // Reset all bonos when the selected product changes (their linea_ids may change).
      updated[index].bonoId = "";
      updated[index].bonosByUnit = {};
    }
    setLines(updated);
  };

  // Set (or clear) the bono for a specific generated sub-line of a multi-unit product.
  const updateLineBonoByUnit = (index, unitLineaId, bonoId) => {
    const updated = [...lines];
    const line = updated[index] || {};
    const next = { ...(line.bonosByUnit || {}) };
    if (bonoId) next[unitLineaId] = bonoId;
    else delete next[unitLineaId];
    // If legacy `bonoId` still exists (base unit only), migrate it into the map so
    // it doesn't silently override the new per-unit value.
    if (line.bonoId && !(line.key in next)) next[line.key] = line.bonoId;
    updated[index] = { ...line, bonosByUnit: next, bonoId: "" };
    setLines(updated);
  };

  const addLine = () => setLines([...lines, { key: genKey(), serviceId: "", bonoId: "", bonosByUnit: {} }]);

  const removeLine = (index) => {
    if (lines.length <= 1) {
      setLines([{ key: genKey(), serviceId: "", bonoId: "", bonosByUnit: {} }]);
    } else {
      setLines(lines.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="border border-[#1E1E5F] rounded-lg overflow-hidden" data-testid="block-B">
      {renderBlockHeader("B", <Smartphone className="h-4 w-4 text-[#03BEE4]" />, "Móviles Adicionales", !pkgAValid, disabledTip)}
      {openBlocks.B && pkgAValid && (
        <div className="p-4 space-y-3 bg-[#0A0A35]/30">
          {/* New mobile lines from current quote */}
          {existingLines && existingLines.length > 0 && lines.some(l => l.serviceId) && (
            <Label className="text-slate-500 text-xs uppercase tracking-wider">En este presupuesto</Label>
          )}
          {lines.map((line, i) => (
            <div key={line.key} className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select value={line.serviceId || "__empty__"} onValueChange={v => updateLine(i, "serviceId", v === "__empty__" ? "" : v)}>
                    <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-9 text-sm" data-testid={`mob-b-select-${i}`}>
                      <SelectValue placeholder={placeholder} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                      <SelectItem value="__empty__" className="text-slate-400 text-sm">— Sin selección —</SelectItem>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-white text-sm">
                          {p.nombre} — {fmtC(priceOf(p))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {line.serviceId && getProduct(line.serviceId) && (
                  <span className="text-slate-500 text-xs min-w-[65px] text-right">
                    {fmtC(priceOf(getProduct(line.serviceId)))}
                  </span>
                )}
                <button onClick={() => removeLine(i)} className="text-red-400 hover:text-red-300 p-1" data-testid={`mob-b-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {line.serviceId && (
                <div className="ml-6 pl-3 border-l-2 border-[#1E1E5F]/50 space-y-1">
                  {!bonoCheck.permitido ? (
                    <p className="text-xs text-red-400/70 flex items-center gap-1 py-1">
                      <AlertTriangle className="h-3 w-3" /> {bonoCheck.mensaje}
                    </p>
                  ) : (() => {
                    // Per-unit bono selectors: show N rows when
                    // `unidades_por_seleccion > 1` so each generated sub-line can
                    // carry its own bono. Legacy singleton `line.bonoId` maps to
                    // the base unit for back-compat.
                    const prod = getProduct(line.serviceId);
                    const units = prod?.unidades_por_seleccion || 1;
                    const bonosMap = (line.bonosByUnit && Object.keys(line.bonosByUnit).length > 0)
                      ? line.bonosByUnit
                      : (line.bonoId ? { [line.key]: line.bonoId } : {});
                    return (
                      <>
                        {Array.from({ length: units }).map((_, u) => {
                          const uLid = u === 0 ? line.key : `${line.key}_u${u + 1}`;
                          const value = bonosMap[uLid] || "";
                          const label = units > 1 ? `Línea ${u + 1}:` : '+';
                          return (
                            <div key={uLid} className="flex items-center gap-2">
                              <span className="text-slate-500 text-xs whitespace-nowrap min-w-[55px]">
                                {label}
                              </span>
                              <Select
                                value={value || "__none__"}
                                onValueChange={v => updateLineBonoByUnit(i, uLid, v === "__none__" ? "" : v)}
                              >
                                <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-8 text-xs flex-1" data-testid={`bono-b-select-${i}-u${u}`}>
                                  <SelectValue placeholder="Añadir bono internacional" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                                  <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin bono —</SelectItem>
                                  {bonoProducts.map(b => (
                                    <SelectItem key={b.id} value={b.id} className="text-white text-sm">
                                      {b.nombre} — {fmtC(priceOf(b))}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {value && getProduct(value) && (
                                <span className="text-slate-500 text-xs min-w-[55px] text-right">{fmtC(priceOf(getProduct(value)))}</span>
                              )}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          ))}

          {lines.length < maxLines && (
            <Button variant="ghost" size="sm" onClick={addLine} className="text-[#03BEE4] text-xs h-7" data-testid="add-mob-b-line">
              <Plus className="h-3 w-3 mr-1" />+ Añadir línea móvil
            </Button>
          )}

          {existingLines && existingLines.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#1E1E5F]/50">
              <Label className="text-slate-500 text-xs uppercase tracking-wider">Ya contratadas</Label>
              {existingLines.map(el => {
                const elKey = el.linea_id || el.id;
                return (
                  <div key={el.id} className="space-y-1 mt-2">
                    <div className="flex items-center gap-2 bg-[#1B1B4B]/20 p-2 rounded">
                      <span className="text-slate-300 text-sm flex-1">
                        {el.nombre_servicio} <span className="text-green-400 text-xs">(activa)</span>
                      </span>
                    </div>
                    {bonoCheck.permitido && (
                      <div className="ml-6 pl-3 border-l-2 border-[#1E1E5F]/50">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 text-xs whitespace-nowrap">+</span>
                          <Select
                            value={existingBonos[elKey] || "__none__"}
                            onValueChange={v => setExistingBonos(prev => ({ ...prev, [elKey]: v === "__none__" ? "" : v }))}
                          >
                            <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white h-8 text-xs flex-1" data-testid={`existing-bono-${el.id}`}>
                              <SelectValue placeholder="Añadir bono internacional" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0A0A35] border-[#1E1E5F] max-h-60">
                              <SelectItem value="__none__" className="text-slate-400 text-sm">— Sin bono —</SelectItem>
                              {bonoProducts.map(b => (
                                <SelectItem key={b.id} value={b.id} className="text-white text-sm">
                                  {b.nombre} — {fmtC(priceOf(b))}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
